import { useEffect, useState } from "react";
import { Joyride, STATUS, ACTIONS, EVENTS } from "react-joyride";
import axios from "axios";
import { API } from "@/App";

/**
 * DashboardTour — 8-step React Joyride v3 walkthrough for new business owners.
 *
 * v3 API notes (differs from v2):
 *   - Behaviour defaults (showSkipButton, showProgress, skipBeacon,
 *     blockTargetInteraction, colours) live in the top-level `options` prop
 *   - `styles.buttonNext` is renamed `styles.buttonPrimary`
 *   - Callback prop is `onEvent`, not `callback`
 *   - `data.status`/`data.action`/`data.type` shape is unchanged
 *
 * Robustness:
 *   - `activeSteps` filter runs INSIDE an effect that keys on `running` so it
 *     evaluates the DOM AFTER the surrounding dashboard has committed. Fixes
 *     the ?startTour=1 deep-link bug where the tour would start with an
 *     empty step list because Joyride mounted before the checklist did.
 *   - Steps whose target isn't in the DOM at run-time are dropped silently
 *     rather than freezing the tour.
 */
const STEPS = [
  {
    target: '[data-testid="dashboard-get-started-checklist"]',
    title: "Welcome to your Business Dashboard",
    content: "This is where you'll manage your business, storefront, products, visibility and performance. Let's take a 60-second tour.",
    placement: "bottom",
  },
  {
    target: '[data-testid="dashboard-business-header"]',
    title: "Complete your profile",
    content: "Add your business information, description, contact details and everything customers need to discover you.",
    placement: "bottom",
  },
  {
    target: '[data-testid="dashboard-logo-section"]',
    title: "Make your business stand out",
    content: "Upload your logo and cover images — high-quality photos are the #1 driver of trust.",
    placement: "top",
  },
  {
    target: '[data-testid="tab-products"]',
    title: "Add what you sell",
    content: "Add your products or services here so customers can discover and buy from you on Blinterest.",
    placement: "bottom",
  },
  {
    target: '[data-testid="dashboard-blinterest-section"]',
    title: "Get discovered on Blinterest",
    content: "Publish Drops here — visual posts that land in the community's Discover feed and drive new customers to you.",
    placement: "top",
  },
  {
    target: '[data-testid="blinterest-analytics-card"]',
    title: "See what's working",
    content: "Track how customers are discovering and interacting with your business — views, keeps, clicks and more.",
    placement: "top",
  },
  {
    target: '[data-testid="tab-orders"]',
    title: "Manage orders & customers",
    content: "Every order and customer interaction lives here. Come back often once your first sale rolls in.",
    placement: "bottom",
  },
  {
    target: '[data-testid="dashboard-get-started-checklist"]',
    title: "You're ready",
    content: "Come back to the Business Academy any time you need help. Now let's get building.",
    placement: "bottom",
  },
];

export default function DashboardTour({ business, running, onFinish }) {
  const businessId = business?.business_id;
  // Steps are resolved AFTER the tour is started so the surrounding dashboard
  // has time to render every target element.
  const [activeSteps, setActiveSteps] = useState([]);

  useEffect(() => {
    if (!running) {
      setActiveSteps([]);
      return undefined;
    }
    let cancelled = false;
    const resolve = () => {
      if (cancelled || typeof document === "undefined") return;
      const survivors = STEPS.filter((s) => {
        try {
          return Boolean(document.querySelector(s.target));
        } catch {
          return false;
        }
      });
      setActiveSteps(survivors);
    };
    // Give the dashboard's own mount effects a beat before we snapshot
    const t = setTimeout(resolve, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [running]);

  const persist = async (status, step) => {
    if (!businessId) return;
    try {
      await axios.post(
        `${API}/academy/progress/${businessId}/tour`,
        { status, step },
        { withCredentials: true },
      );
    } catch {
      /* silent — tour still works even if persistence fails */
    }
  };

  const handleEvent = (data) => {
    const { status, action, index, type, size } = data;
    if (status === STATUS.FINISHED) {
      persist("completed", index);
      onFinish?.("finished");
      return;
    }
    if (status === STATUS.SKIPPED || action === ACTIONS.SKIP) {
      persist("skipped", index);
      onFinish?.("skipped");
      return;
    }
    if (action === ACTIONS.CLOSE) {
      persist("skipped", index);
      onFinish?.("closed");
      return;
    }
    // Skip the in_progress persist on the LAST step so it doesn't race
    // with the FINISHED status that arrives moments later — otherwise
    // tour_completed on the checklist never flips to done.
    if (type === EVENTS.STEP_AFTER
        && action === ACTIONS.NEXT
        && index + 1 < size) {
      persist("in_progress", index + 1);
    }
  };

  if (!running || activeSteps.length === 0) return null;

  return (
    <Joyride
      steps={activeSteps}
      run={running}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={{
        arrowColor: "#0A192F",
        backgroundColor: "#0A192F",
        overlayColor: "rgba(10, 25, 47, 0.75)",
        primaryColor: "#F5C518",
        textColor: "#ffffff",
        zIndex: 10000,
        showProgress: true,
        buttons: ["skip", "back", "primary"],
        skipBeacon: true,
        blockTargetInteraction: false,
        overlayClickAction: "next",
        scrollOffset: 100,
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip Tour",
      }}
      styles={{
        tooltip: {
          borderRadius: 16,
          fontFamily: "inherit",
        },
        tooltipTitle: {
          fontSize: 18,
          fontWeight: 600,
          fontFamily: "inherit",
          color: "#F5C518",
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.9)",
        },
        buttonPrimary: {
          backgroundColor: "#F5C518",
          color: "#0A192F",
          fontWeight: 600,
          borderRadius: 999,
          padding: "8px 20px",
        },
        buttonBack: {
          color: "rgba(255,255,255,0.7)",
        },
        buttonSkip: {
          color: "rgba(255,255,255,0.5)",
        },
      }}
    />
  );
}
